import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Alert, TextField } from '@mui/material';
import { useDmpTranslation } from '@/hooks/useDmpTranslation';
import styled from 'styled-components';
import { Button } from '@/components/UI/Button';
import { H4, H5 } from '@/components/UI/Typography/Heading';
import { errorHandler, successHandler } from '@/utils/toastHandler';
import * as api from '@/api/profileChanges.api';
import type { ProfileChangeMetadata, ProfileChangeRequest, ProfileChangeValues } from '@/types/profileChanges';
import { changedProfileValues, requiredProfileChangeValid, validProfileEvidence } from './helpers';
import AddressFields from './AddressFields';

const PROFILE_FIELD_KEYS = [
  'acc_coname', 'acc_firstName', 'acc_lastName', 'acc_address', 'acc_BR', 'acc_LEI',
  'address_floor', 'address_street', 'address_district', 'province', 'city',
] as const;
type ProfileFieldKey = typeof PROFILE_FIELD_KEYS[number];
const isProfileFieldKey = (key: string): key is ProfileFieldKey => PROFILE_FIELD_KEYS.some(field => field === key);

const Section = styled.section`
  background: white; max-width: 930px; padding: 32px; margin-top: 24px;
  font-size: ${p => p.theme.fontSizes.base}; line-height: 1.6; overflow-wrap: anywhere;
  .fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; margin: 24px 0; }
  .address { grid-column: 1 / -1; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; }
  .actions { display: flex; flex-wrap: wrap; gap: 12px; margin: 20px 0; }
  button { min-height: 44px; height: auto; white-space: normal; }
  article { border-top: 1px solid #ddd; padding: 20px 0; }
  dl { margin: 12px 0; } dt { font-weight: bold; } dd { margin: 0 0 12px; }
  input[type=file] { display: block; max-width: 100%; margin: 12px 0; font: inherit; }
  @media (max-width: 640px) { padding: 16px; .fields, .address { grid-template-columns: 1fr; } }
`;
interface FormValues { values: Record<string, string>; reason: string }

export default function ProfileChangeRequests({ onUpdated, countryCode }: { onUpdated?: () => void; countryCode?: string }) {
  const { t } = useDmpTranslation('profileChanges');
  const [metadata, setMetadata] = useState<ProfileChangeMetadata>();
  const [items, setItems] = useState<ProfileChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<FormValues>();
  const [evidence, setEvidence] = useState<File>();
  const [withdrawId, setWithdrawId] = useState<number>();
  const { control, handleSubmit, reset, setError, formState: { errors } } = useForm<FormValues>({ defaultValues: { values: {}, reason: '' } });
  const load = useCallback(async () => {
    setLoading(true); setFailed(false);
    try {
      const [meta, requests] = await Promise.all([api.getProfileChangeMetadata(), api.getProfileChangeRequests()]);
      setMetadata(meta.data.data); setItems(requests.data.data.items);
      reset({ values: Object.fromEntries(meta.data.data.fields.map(field => [field.key, field.value ?? ''])), reason: '' });
    } catch { setFailed(true); }
    finally { setLoading(false); }
  }, [reset]);
  useEffect(() => { void load(); }, [load]);
  const active = items.some(item => item.status === 'pending' || item.status === 'draft');
  const label = (key: string): string => isProfileFieldKey(key)
    ? t(`fields.${key}`)
    : metadata?.fields.find(field => field.key === key)?.label ?? key;
  const changes = preview && metadata ? changedProfileValues(metadata.fields, preview.values) : {};
  const act = async (operation: () => Promise<unknown>) => {
    setBusy(true);
    try { await operation(); setPreview(undefined); setEvidence(undefined); setWithdrawId(undefined); successHandler(t('saved')); await load(); onUpdated?.(); }
    catch { errorHandler(t('actionFailed')); }
    finally { setBusy(false); }
  };
  const review = (values: FormValues) => {
    if (!metadata || !Object.keys(changedProfileValues(metadata.fields, values.values)).length) { setError('reason', { message: t('noChanges') }); return; }
    setPreview(values);
  };
  const differences = (before: ProfileChangeValues, proposed: ProfileChangeValues) => <dl>{Object.keys(proposed).map(key => <div key={key}>
    <dt>{label(key)}</dt><dd>{t('before')}: {before[key]?.split('@@_@@').join(' / ') || t('empty')}<br />{t('after')}: {proposed[key]?.split('@@_@@').join(' / ') || t('empty')}</dd>
  </div>)}</dl>;
  const download = async (id: number) => {
    setBusy(true);
    try {
      const response = await api.downloadProfileChangeEvidence(id);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a'); link.href = url;
      const ext = response.data.type === 'application/pdf' ? 'pdf' : response.data.type === 'image/png' ? 'png' : 'jpg';
      link.download = `profile-change-${id}.${ext}`; link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { errorHandler(t('actionFailed')); } finally { setBusy(false); }
  };
  return <Section aria-labelledby="profile-change-title" aria-busy={loading || busy}>
    <H4 id="profile-change-title">{t('title')}</H4><p>{t('description')}</p>
    {loading ? <p role="status">{t('loading')}</p> : failed ? <Alert severity="error">{t('loadFailed')}<Button type="button" variant="primary-invert" onClick={load}>{t('retry')}</Button></Alert> : <>
      <Alert severity="info">{t(metadata?.requires_review ? 'reviewNotice' : 'directNotice')}</Alert>
      <p>{t('identityHint')}</p>
      {!!metadata?.manual_review_fields?.length && <Alert severity="info">{t('manualReviewNotice')}</Alert>}
      {!metadata?.can_submit && <Alert severity="info">{t('unavailable')}</Alert>}
      {active && <p role="status">{t('activeNotice')}</p>}
      {metadata?.can_submit && !active && <form onSubmit={handleSubmit(review)}>
        {!preview ? <>
          <div className="fields">{metadata.fields.map(field => <Controller key={field.key} name={`values.${field.key}`} control={control}
            rules={{ validate: value => requiredProfileChangeValid(field, value) || t('required'), maxLength: { value: field.max_length, message: t('tooLong') } }}
            render={({ field: input, fieldState }) => field.type === 'address' && field.segments?.length ?
              <AddressFields field={field} value={input.value ?? ''} onChange={input.onChange} onBlur={input.onBlur}
                countryCode={countryCode} disabled={busy} error={fieldState.error?.message} />
              : <TextField {...input} label={label(field.key)} fullWidth disabled={busy}
              multiline={field.key === 'acc_address'} error={!!fieldState.error} helperText={fieldState.error?.message} inputProps={{ maxLength: field.max_length }} />} />)}</div>
          <Controller name="reason" control={control} rules={{ validate: value => !!value.trim() || t('required'), maxLength: { value: 2000, message: t('tooLong') } }}
            render={({ field }) => <TextField {...field} label={t('reason')} fullWidth multiline minRows={3} disabled={busy} error={!!errors.reason} helperText={errors.reason?.message} inputProps={{ maxLength: 2000 }} />} />
          <label htmlFor="profile-change-evidence">{t('evidence')}</label>
          <input id="profile-change-evidence" type="file" accept=".pdf,.jpg,.jpeg,.png" disabled={busy} onChange={event => {
            const file = event.target.files?.[0];
            if (file && !validProfileEvidence(file)) { errorHandler(t('invalidEvidence')); event.target.value = ''; setEvidence(undefined); return; }
            setEvidence(file);
          }} />
          {evidence && <p>{evidence.name}</p>}
          <div className="actions"><Button type="submit" variant="primary" disabled={busy}>{t('preview')}</Button></div>
        </> : <>
          <H5>{t('preview')}</H5>{differences(Object.fromEntries(metadata.fields.map(field => [field.key, field.value])), changes)}
          <p>{t('reason')}: {preview.reason}</p>{evidence && <p>{t('evidence')}: {evidence.name}</p>}
          <div className="actions">
            <Button type="button" variant="primary" disabled={busy} onClick={() => act(() => api.createProfileChangeRequest(changes, preview.reason.trim(), 'pending', evidence))}>{t(metadata.requires_review ? 'submit' : 'apply')}</Button>
            <Button type="button" variant="primary-invert" disabled={busy} onClick={() => act(() => api.createProfileChangeRequest(changes, preview.reason.trim(), 'draft', evidence))}>{t('saveDraft')}</Button>
            <Button type="button" variant="primary-invert" disabled={busy} onClick={() => setPreview(undefined)}>{t('edit')}</Button>
          </div>
        </>}
      </form>}
      <H5>{t('history')}</H5><div className="actions"><Button type="button" variant="primary-invert" disabled={busy} onClick={async () => { await load(); onUpdated?.(); }}>{t('refresh')}</Button></div>
      {!items.length && <p>{t('noHistory')}</p>}
      {items.map(item => <article key={item.id}>
        <H5>#{item.id} — {t(`status.${item.status}`)}</H5><p>{item.created_at}</p>
        {differences(item.before_values, item.proposed_values)}<p>{t('reason')}: {item.reason}</p>
        {item.review_reason && <Alert severity={item.status === 'rejected' ? 'warning' : 'info'}>{t('reviewReason')}: {item.review_reason}</Alert>}
        {item.reviewed_at && <p>{t('reviewedAt')}: {item.reviewed_at}</p>}
        <div className="actions">
          {item.evidence_available && <Button type="button" variant="primary-invert" disabled={busy} onClick={() => download(item.id)}>{t('downloadEvidence')}</Button>}
          {item.status === 'draft' && <Button type="button" variant="primary" disabled={busy} onClick={() => act(() => api.submitProfileChangeRequest(item.id))}>{t('submit')}</Button>}
          {(item.status === 'pending' || item.status === 'draft') && <Button type="button" variant="primary-invert" disabled={busy} onClick={() => setWithdrawId(item.id)}>{t('withdraw')}</Button>}
          {(item.status === 'rejected' || item.status === 'withdrawn') && metadata?.can_submit && !active && <Button type="button" variant="primary-invert" disabled={busy} onClick={() => {
            reset({ values: { ...Object.fromEntries(metadata.fields.map(field => [field.key, field.value ?? ''])), ...Object.fromEntries(Object.entries(item.proposed_values).map(([key, value]) => [key, value ?? ''])) }, reason: item.reason });
            setPreview(undefined); setEvidence(undefined); document.getElementById('profile-change-title')?.scrollIntoView({ block: 'start' });
          }}>{t('reapply')}</Button>}
        </div>
        {withdrawId === item.id && <Alert severity="warning">{t('withdrawConfirm')}<div className="actions">
          <Button type="button" variant="danger" disabled={busy} onClick={() => act(() => api.withdrawProfileChangeRequest(item.id))}>{t('confirmWithdraw')}</Button>
          <Button type="button" variant="primary-invert" disabled={busy} onClick={() => setWithdrawId(undefined)}>{t('cancel')}</Button>
        </div></Alert>}
      </article>)}
    </>}
  </Section>;
}
